import './Score.scss';
import { useEffect, useRef, useState } from 'react';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import Module from '../Module';
import StatusService from '../../../../../Services/StatusService';
import DataPanel from '../../../../../Components/DataPanel/DataPanel';
import LoadingBar from '../../../../../Components/LoadingBar/LoadingBar';
import { TranslateTriggers } from '../../../../../Components/Utilities/TriggerTexts/DefaultTriggers';
import XpektorButton from '../../../../../Components/XpektorButton/XpektorButton';

const gT = (t, key) => t(`company-info-modules.score${key}`);

/* eslint-disable react/prop-types */
export default function Score({ client, onPdfClick, t }) {
  const [score, setScore] = useState({});
  const [overallGrade, setOverallGrade] = useState('');
  const [economicTriggers, setEconomicTriggers] = useState([]);
  const [regulatoryTriggers, setRegulatoryTriggers] = useState([]);
  const [managementTriggers, setManagementTriggers] = useState([]);
  const [accountTriggers, setAccountTriggers] = useState([]);

  const economicRef = useRef();
  const regulatoryRef = useRef();
  const managementRef = useRef();
  const accountRef = useRef();

  const smooth = { behavior: 'smooth' };

  const gradeBackgroundColors = {
    A: '#c3e9c4',
    B: 'rgb(225, 255, 191)',
    C: 'rgb(255, 253, 231)',
    D: 'rgb(255, 234, 195)',
    E: 'rgb(255, 208, 208)',
    '-': 'rgb(233, 233, 233)',
  };

  const gradeDescriptions = {
    A: gT(t, '.grade-description.A'),
    B: gT(t, '.grade-description.B'),
    C: gT(t, '.grade-description.C'),
    D: gT(t, '.grade-description.D'),
    E: gT(t, '.grade-description.E'),
    '-': gT(t, '.grade-description._'),
  };

  const checkForNull = (grade) => (grade === null ? '-' : grade);

  useEffect(() => {
    const getScore = async () => {
      await StatusService.getScoreLatest(client.id)
        .then((response) => {
          setEconomicTriggers(
            TranslateTriggers(response.data.economicTriggers)
          );
          setRegulatoryTriggers(
            TranslateTriggers(response.data.regulatoryTriggers)
          );
          setManagementTriggers(
            TranslateTriggers(response.data.managementTriggers)
          );
          setAccountTriggers(TranslateTriggers(response.data.accountTriggers));
          setOverallGrade(checkForNull(response.data.overallScore));
          setScore(response.data);
        })
        .catch(() => setScore(null));
    };
    if (Object.keys(client).length !== 0) {
      getScore();
    }
  }, [client]);

  const mapTriggers = (triggers) => {
    return triggers.map((trigger) => (
      <DataPanel.ExpandableTriggerEntry
        name={trigger.texts.name}
        info={trigger.texts.info}
        key={trigger.id}
        actions={trigger.texts.tips}
        t={t}
      />
    ));
  };

  const anyDataMissing = () => {
    return (
      score.economicScore === null ||
      score.managementScore === null ||
      score.regulatoryScore === null ||
      score.accountScore === null
    );
  };

  const renderOverallInfoCategory = (title, grade, onClick) => {
    return (
      <button className="summary-grade-button" onClick={onClick} type="button">
        <p
          className={`summary-grade grade-${grade}`}
          style={{
            backgroundColor: gradeBackgroundColors[grade],
          }}
        >
          {grade}
        </p>
        <p className="summary-grade-title">{title}</p>
      </button>
    );
  };

  return (
    <Module>
      {score == null ? (
        <p className="module-empty-text">{gT(t, '.module-empty-text')}</p>
      ) : Object.keys(score).length === 0 ? (
        <LoadingBar active status={gT(t, '.loading-bar-status')} relative />
      ) : (
        <>
          <Module.Nav>
            <div />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <XpektorButton
                icon={faDownload}
                content={gT(t, '.xpektor-btn.save-as-pdf')}
                onClick={() =>
                  onPdfClick(() => document.getElementById('score-pdf-wrapper'))
                }
              />
            </div>
          </Module.Nav>
          <div className="score-wrapper" id="score-pdf-wrapper">
            <div className="score-header">
              <div
                className={`score-gauge ${
                  !client.externalConnectionActive &&
                  !client.bankConnectionActive
                    ? 'score-gauge-disconnected grade--'
                    : `grade-${overallGrade}`
                }`}
              >
                <p
                  className={`score-gauge-grade ${
                    !client.externalConnectionActive &&
                    !client.bankConnectionActive
                      ? 'grade--'
                      : `grade-${overallGrade}`
                  }`}
                >
                  {overallGrade === '-' ? '?' : overallGrade}
                </p>
              </div>
              <div className="score-overall-info">
                <p className="overall-info-title">{gT(t, '.over-all-title')}</p>
                <p className="overall-info-text">
                  {gradeDescriptions[overallGrade]}
                </p>
                {anyDataMissing() && (
                  <p className="overall-info-disclaimer">
                    <b>{gT(t, '.overall-info-disclaimer')}</b>
                  </p>
                )}
              </div>
              <div className="score-grade-summary">
                <p className="grade-summary-title">
                  {gT(t, '.grade-summary-title')}
                </p>
                {renderOverallInfoCategory(
                  gT(t, '.overall-info-category.equity-liquidity'),
                  checkForNull(score.economicScore),
                  () => economicRef.current.scrollIntoView(smooth)
                )}
                {renderOverallInfoCategory(
                  gT(t, '.overall-info-category.management'),
                  checkForNull(score.managementScore),
                  () => managementRef.current.scrollIntoView(smooth)
                )}
                {renderOverallInfoCategory(
                  gT(t, '.overall-info-category.regulatory'),
                  checkForNull(score.regulatoryScore),
                  () => regulatoryRef.current.scrollIntoView(smooth)
                )}
                {renderOverallInfoCategory(
                  gT(t, '.overall-info-category.account-analytics'),
                  checkForNull(score.accountScore),
                  () => accountRef.current.scrollIntoView(smooth)
                )}
              </div>
            </div>
            <div className="score-content">
              <div className="score-content-column">
                <DataPanel
                  title={gT(t, '.data-panel-title.equity-liquidity')}
                  grade={checkForNull(score.economicScore)}
                  givenRef={economicRef}
                >
                  {economicTriggers.length > 0 ? (
                    mapTriggers(economicTriggers)
                  ) : score.economicScore === null ? (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-score-data')}
                      t={t}
                    />
                  ) : (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-demerit')}
                      t={t}
                    />
                  )}
                </DataPanel>
                <DataPanel
                  title={gT(t, '.data-panel-title.regulatory')}
                  grade={checkForNull(score.regulatoryScore)}
                  givenRef={regulatoryRef}
                >
                  {regulatoryTriggers.length > 0 ? (
                    mapTriggers(regulatoryTriggers)
                  ) : score.regulatoryScore === null ? (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-score-data')}
                      t={t}
                    />
                  ) : (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-demerit')}
                      t={t}
                    />
                  )}
                </DataPanel>
              </div>
              <div className="score-content-column">
                <DataPanel
                  title={gT(t, '.data-panel-title.management')}
                  grade={checkForNull(score.managementScore)}
                  givenRef={managementRef}
                >
                  {managementTriggers.length > 0 ? (
                    mapTriggers(managementTriggers)
                  ) : score.managementScore === null ? (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-score-data')}
                      t={t}
                    />
                  ) : (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-demerit')}
                      t={t}
                    />
                  )}
                </DataPanel>
                <DataPanel
                  title={gT(t, '.data-panel-title.account-analytics')}
                  grade={checkForNull(score.accountScore)}
                  givenRef={accountRef}
                >
                  {accountTriggers.length > 0 ? (
                    mapTriggers(accountTriggers)
                  ) : score.accountScore === null ? (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-score-data')}
                      t={t}
                    />
                  ) : (
                    <DataPanel.ExpandableTriggerEntry
                      name={gT(t, '.no-demerit')}
                      t={t}
                    />
                  )}
                </DataPanel>
              </div>
            </div>
          </div>
        </>
      )}
    </Module>
  );
}
